import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../../AuthContext';
import { useEmulation } from '../../../../contexts/EmulationContext';
import { getAllUsers } from '../../../../api/brandApi';
import { removeUser, sendWelcomeEmail, toggleSuperAdmin, toggleCustomerSuccess } from '../../../../api/authApi';
import TendModal from '../../../brand/TendModal';
import SheetMessage from '../shared/SheetMessage';

interface EmulationBlockProps {
  /** Load users only while the profile view is showing. */
  active: boolean;
}

/**
 * Superadmin system-users list (emulation + welcome email + admin toggle),
 * redesigned from the old absolute drop-up into an in-sheet expandable menu.
 * The non-superadmin mock-user switcher was intentionally dropped.
 */
const EmulationBlock: React.FC<EmulationBlockProps> = ({ active }) => {
  const { user } = useAuth();
  const { isEmulating, emulatedUser, emulateUser, exitEmulation, canEmulate } = useEmulation();

  const [expanded, setExpanded] = useState(false);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sendingWelcomeEmail, setSendingWelcomeEmail] = useState<string | null>(null);
  const [togglingAdmin, setTogglingAdmin] = useState<string | null>(null);
  const [togglingCS, setTogglingCS] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  // Remove-user flow: the trash action opens a TendModal are-you-sure
  // (brand-delete pattern) before anything happens.
  const [removeTarget, setRemoveTarget] = useState<any | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  useEffect(() => {
    if (canEmulate && active && expanded && allUsers.length === 0) {
      loadAllUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEmulate, active, expanded]);

  const loadAllUsers = async () => {
    try {
      setLoadingUsers(true);
      setUsersError(null);
      const users = await getAllUsers();
      setAllUsers(users);
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const flash = (kind: 'ok' | 'err', text: string) => {
    setNotice({ kind, text });
    setTimeout(() => setNotice(null), kind === 'ok' ? 3000 : 5000);
  };

  const handleSendWelcomeEmail = async (targetUser: any) => {
    setSendingWelcomeEmail(targetUser.id);
    try {
      await sendWelcomeEmail(targetUser.id);
      flash('ok', `Welcome email sent to ${targetUser.email}`);
    } catch (err) {
      flash('err', err instanceof Error ? err.message : 'Failed to send welcome email');
    } finally {
      setSendingWelcomeEmail(null);
    }
  };

  const handleToggleSuperAdmin = async (targetUser: any) => {
    const newStatus = !targetUser.is_super_admin;
    setTogglingAdmin(targetUser.id);
    try {
      await toggleSuperAdmin(targetUser.id, newStatus);
      setAllUsers((prev) =>
        prev.map((u) => (u.id === targetUser.id ? { ...u, is_super_admin: newStatus } : u))
      );
      flash('ok', `${targetUser.email} is ${newStatus ? 'now' : 'no longer'} a super admin`);
    } catch (err) {
      flash('err', err instanceof Error ? err.message : 'Failed to update admin status');
    } finally {
      setTogglingAdmin(null);
    }
  };

  const handleToggleCustomerSuccess = async (targetUser: any) => {
    const newStatus = !targetUser.is_customer_success;
    setTogglingCS(targetUser.id);
    try {
      await toggleCustomerSuccess(targetUser.id, newStatus);
      setAllUsers((prev) =>
        prev.map((u) => (u.id === targetUser.id ? { ...u, is_customer_success: newStatus } : u))
      );
      flash('ok', `${targetUser.email} is ${newStatus ? 'now' : 'no longer'} Customer Success`);
    } catch (err) {
      flash('err', err instanceof Error ? err.message : 'Failed to update Customer Success status');
    } finally {
      setTogglingCS(null);
    }
  };

  const handleRemoveUser = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      const result = await removeUser(removeTarget.id);
      setAllUsers((prev) => prev.filter((u) => u.id !== removeTarget.id));
      setRemoveTarget(null);
      flash('ok', result.message);
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Failed to remove user');
    } finally {
      setRemoving(false);
    }
  };

  const handleEmulation = async (targetUser: any) => {
    try {
      if (targetUser.id === user?.id || (isEmulating && emulatedUser?.id === targetUser.id)) {
        exitEmulation();
        return;
      }
      await emulateUser({
        id: targetUser.id,
        email: targetUser.email,
        username: targetUser.username,
        full_name: targetUser.full_name,
        is_super_admin: targetUser.is_super_admin || false,
        brands: [], // fetched by the emulation context
      });
    } catch (err) {
      console.error('Failed to toggle emulation:', err);
    }
  };

  if (!canEmulate) return null;

  const filteredUsers = allUsers.filter((u) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (u.full_name || '').toLowerCase().includes(query) ||
      (u.username || '').toLowerCase().includes(query) ||
      (u.email || '').toLowerCase().includes(query)
    );
  });

  return (
    <div>
      <button
        type="button"
        className="tend-menu-item"
        onClick={() => setExpanded((v) => !v)}
        style={{ padding: '8px 0' }}
      >
        <span className="tend-micro" style={{ flex: 1, textAlign: 'left' }}>
          System Users {isEmulating && '· EMULATING'}
        </span>
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          style={{
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform .25s',
          }}
        >
          <path strokeLinecap="round" d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
          {notice && <SheetMessage kind={notice.kind}>{notice.text}</SheetMessage>}

          <input
            className="tend-input"
            style={{ padding: '8px 12px', fontSize: 13 }}
            placeholder="Search users"
            aria-label="Search users"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {isEmulating && (
            <button
              type="button"
              className="tend-menu-item"
              onClick={() => exitEmulation()}
              style={{ color: 'var(--accent-blue-soft)' }}
            >
              ← Return to your account
            </button>
          )}

          {loadingUsers && (
            <div className="tend-progress">
              <i style={{ width: '40%' }} />
            </div>
          )}
          {usersError && <SheetMessage kind="err">{usersError}</SheetMessage>}

          <div className="tend-menu" style={{ padding: 0, maxHeight: 260 }}>
            {filteredUsers.map((systemUser) => {
              const isSelf = systemUser.id === user?.id;
              const isActive = isEmulating
                ? emulatedUser?.id === systemUser.id
                : isSelf;
              return (
                <div
                  key={systemUser.id}
                  className={`tend-menu-item${isActive ? ' is-active' : ''}`}
                  onClick={() => handleEmulation(systemUser)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleEmulation(systemUser);
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {systemUser.full_name || systemUser.username}
                      {isSelf && ' (you)'}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        opacity: 0.7,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {systemUser.email}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="tend-btn tend-btn--icon tend-btn--icon-sm"
                    title={
                      systemUser.is_super_admin
                        ? 'Revoke super admin'
                        : 'Grant super admin'
                    }
                    aria-label="Toggle super admin"
                    disabled={togglingAdmin === systemUser.id || isSelf}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleSuperAdmin(systemUser);
                    }}
                    style={
                      systemUser.is_super_admin
                        ? { color: 'var(--accent-blue-soft)' }
                        : undefined
                    }
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 3l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V6l7-3z"
                      />
                    </svg>
                  </button>

                  <button
                    type="button"
                    className="tend-btn tend-btn--icon tend-btn--icon-sm"
                    title={
                      systemUser.is_customer_success
                        ? 'Revoke customer success'
                        : 'Grant customer success'
                    }
                    aria-label="Toggle customer success"
                    disabled={togglingCS === systemUser.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleCustomerSuccess(systemUser);
                    }}
                    style={
                      systemUser.is_customer_success
                        ? { color: 'var(--signal-scribe)' }
                        : undefined
                    }
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 14v-2a8 8 0 0116 0v2" />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 15a2 2 0 012-2h1v5H5a2 2 0 01-2-2v-1zm18 0a2 2 0 00-2-2h-1v5h1a2 2 0 002-2v-1z"
                      />
                      <path strokeLinecap="round" d="M17 19a3 3 0 01-3 2h-2" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    className="tend-btn tend-btn--icon tend-btn--icon-sm"
                    title="Send welcome email"
                    aria-label="Send welcome email"
                    disabled={sendingWelcomeEmail === systemUser.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSendWelcomeEmail(systemUser);
                    }}
                  >
                    {sendingWelcomeEmail === systemUser.id ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" d="M12 3v3m0 12v3m9-9h-3M6 12H3" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 8l9 6 9-6M4 6h16a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 011-1z"
                        />
                      </svg>
                    )}
                  </button>

                  <button
                    type="button"
                    className="tend-btn tend-btn--icon tend-btn--icon-sm"
                    title={systemUser.is_super_admin ? 'Demote this superadmin before removing' : 'Remove user'}
                    aria-label="Remove user"
                    disabled={systemUser.id === user?.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setRemoveError(null);
                      setRemoveTarget(systemUser);
                    }}
                    style={{ color: '#f87171' }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 7l-.9 12.1A2 2 0 0116.1 21H7.9a2 2 0 01-2-1.9L5 7m5 4v6m4-6v6M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2"
                      />
                    </svg>
                  </button>
                </div>
              );
            })}
            {!loadingUsers && filteredUsers.length === 0 && (
              <div style={{ padding: 10, fontSize: 12, color: 'var(--text-3)' }}>
                No users match.
              </div>
            )}
          </div>
        </div>
      )}

      {removeTarget && (
        <TendModal
          title="Remove user"
          subtitle={removeTarget.email}
          onClose={() => { if (!removing) setRemoveTarget(null); }}
          width={420}
        >
          <div
            style={{
              borderRadius: 12,
              border: '1px solid rgba(248,113,113,.35)',
              background: 'rgba(248,113,113,.10)',
              padding: 12,
            }}
          >
            <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#f87171', marginBottom: 6 }}>
              This removes {removeTarget.full_name || removeTarget.username || 'this user'}&apos;s account
            </span>
            <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {[
                'Their login, sessions, and brand access are deleted',
                'Content they created and activity history are kept',
                'This cannot be undone — re-invite to restore access',
              ].map((line) => (
                <li key={line} style={{ fontSize: 12.5, color: 'var(--text-2, rgba(255,255,255,.78))', lineHeight: 1.45 }}>
                  {line}
                </li>
              ))}
            </ul>
          </div>

          {removeError && (
            <span className="tend-micro" style={{ color: 'var(--signal-rec-soft)', textTransform: 'none', letterSpacing: 0.4 }}>
              {removeError}
            </span>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4 }}>
            <button
              type="button"
              className="tend-btn tend-btn--glass"
              style={{ padding: '8px 16px', fontSize: 13 }}
              onClick={() => setRemoveTarget(null)}
              disabled={removing}
            >
              Cancel
            </button>
            <button
              type="button"
              className="tend-btn tend-btn--glass"
              style={{
                padding: '8px 16px',
                fontSize: 13,
                color: '#f87171',
                borderColor: 'rgba(248,113,113,.35)',
              }}
              onClick={handleRemoveUser}
              disabled={removing}
            >
              {removing ? 'Removing…' : 'Remove User'}
            </button>
          </div>
        </TendModal>
      )}
    </div>
  );
};

export default EmulationBlock;
